import { ApiError } from "@/lib/api"

/**
 * La carga por archivo: tipos del backend y las decisiones que NO son de
 * pintado.
 *
 * Todo lo que decide si se puede escribir, que se le dice al usuario que va a
 * pasar y como se leen los errores vive aca y no en el componente, por una
 * razon concreta: es exactamente el sitio donde esta pantalla puede mentir.
 * Un boton «Cargar» habilitado sobre una previsualizacion de OTRO archivo
 * escribe algo que nadie reviso, y no falla — que es la familia de defecto que
 * mas caro sale en este producto.
 */

// ---------------------------------------------------------------------------
// Lo que devuelve el backend
// ---------------------------------------------------------------------------

export interface ColumnaPlantilla {
  nombre: string
  tipo: "texto" | "entero" | "decimal" | "fecha" | "booleano" | string
  obligatoria: boolean
  ayuda: string
  alias: string[]
  opciones: string[] | null
}

export interface Plantilla {
  id: string
  nombre: string
  descripcion: string
  /** Campos que identifican una fila ya cargada: recargar ACTUALIZA, no duplica. */
  clave: string[]
  columnas: ColumnaPlantilla[]
  ejemplo: Record<string, string>[]
}

export interface ErrorDeFila {
  linea: number
  columna: string | null
  valor: string | null
  motivo: string
}

export interface Previsualizacion {
  plantilla: string
  separador: string
  filas_leidas: number
  errores: ErrorDeFila[]
  columnas_reconocidas: string[]
  columnas_ignoradas: string[]
  nuevas: number
  existentes: number
  se_escribiria: boolean
  nota: string
}

/**
 * Lo que devuelve la escritura. `creadas` y `actualizadas` estan siempre; el
 * resto depende de la plantilla (`omitidas_sin_rulepack`, `rechazadas_usd`…),
 * y por eso el tipo es abierto: ver `lineasDelResultado`.
 */
export interface ResultadoCarga {
  creadas: number
  actualizadas: number
  errores: ErrorDeFila[]
  columnas_ignoradas: string[]
  [clave: string]: unknown
}

// ---------------------------------------------------------------------------
// Si se puede escribir, y si no, por que
// ---------------------------------------------------------------------------

export interface EstadoDeCarga {
  plantillaId: string | null
  /** Identidad del archivo elegido AHORA (ver `huellaDeArchivo`). */
  archivo: string | null
  /** Sobre que archivo se hizo la previsualizacion que se esta mostrando. */
  previsualizacionDe: string | null
  previsualizacion: Previsualizacion | null
  motivo: string
  continuarConErrores: boolean
  puedeEditar: boolean
}

export interface Veredicto {
  listo: boolean
  /** Por que no se puede todavia. Vacio cuando `listo`. */
  impedimento: string
  /** Que va a pasar exactamente si se pulsa. Vacio cuando no se puede. */
  consecuencia: string
}

/**
 * Identidad del archivo elegido.
 *
 * El nombre no basta: se corrige el CSV en Excel, se guarda con el mismo
 * nombre y se vuelve a elegir. Con tamaño y fecha de modificacion, ese archivo
 * corregido es OTRO y la previsualizacion anterior deja de valer, que es
 * justamente lo que hay que detectar.
 */
export function huellaDeArchivo(archivo: File | null): string | null {
  if (!archivo) return null
  return `${archivo.name}|${archivo.size}|${archivo.lastModified}`
}

export function puedeCargar(estado: EstadoDeCarga): Veredicto {
  const no = (impedimento: string): Veredicto => ({ listo: false, impedimento, consecuencia: "" })

  if (!estado.puedeEditar) {
    return no("Tu rol puede previsualizar pero no escribir maestros en este cliente.")
  }
  if (!estado.plantillaId) return no("Elige primero qué se va a cargar.")
  if (!estado.archivo) return no("Elige el archivo.")

  // LA COMPROBACION QUE IMPORTA. Si el archivo cambio despues de previsualizar,
  // lo que se ve en pantalla describe otro archivo. Escribir entonces es
  // escribir a ciegas con la apariencia de haber revisado.
  if (!estado.previsualizacion || estado.previsualizacionDe !== estado.archivo) {
    return no("Previsualiza este archivo antes de cargarlo.")
  }

  const p = estado.previsualizacion
  const validas = p.filas_leidas
  const conErrores = p.errores.length

  if (validas === 0) {
    return no(
      conErrores > 0
        ? `Ninguna fila pasó la validación: ${conErrores} ${conErrores === 1 ? "error" : "errores"}. Corrige el archivo y vuelve a subirlo.`
        : "El archivo no trae ninguna fila que cargar.",
    )
  }
  if (conErrores > 0 && !estado.continuarConErrores) {
    return no(
      `${conErrores} ${conErrores === 1 ? "fila tiene" : "filas tienen"} errores. Corrígelas y vuelve a subir el archivo, o marca «continuar con errores» para cargar solo las ${validas} que sí están bien.`,
    )
  }
  if (estado.motivo.trim().length < 3) {
    return no("Escribe el motivo: queda en la pista de auditoría con tu nombre y la fecha.")
  }

  const partes: string[] = []
  if (p.nuevas > 0) partes.push(`se crearán ${p.nuevas}`)
  if (p.existentes > 0) partes.push(`se actualizarán ${p.existentes}`)
  if (conErrores > 0) partes.push(`se omitirán ${conErrores} con error`)

  return {
    listo: true,
    impedimento: "",
    consecuencia: partes.length ? capitalizar(partes.join(", ")) + "." : `Se cargarán ${validas} filas.`,
  }
}

function capitalizar(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

// ---------------------------------------------------------------------------
// El resultado, sin esconder nada
// ---------------------------------------------------------------------------

/** Como se lee cada contador que devuelven los escritores del backend. */
const ETIQUETAS: Record<string, string> = {
  creadas: "creadas",
  actualizadas: "actualizadas",
  omitidas_sin_rulepack: "omitidas: su país no tiene rulepack (solo PE, MX, BR y PA)",
  omitidas_sin_sociedad: "omitidas: este cliente todavía no tiene ninguna sociedad",
  rechazadas_usd: "rechazadas: el dólar es la base y no lleva fila de cotización",
}

/** Contadores que no son contadores: no se listan como tales. */
const NO_SON_CONTADORES = new Set(["errores", "columnas_ignoradas", "archivo", "omitidas_por_error"])

export interface LineaDeResultado {
  clave: string
  etiqueta: string
  cantidad: number
  /** Lo que no entró. Se pinta en aviso aunque la carga «funcionara». */
  esOmision: boolean
}

/**
 * Cada contador del resultado, incluidos los que no se esperaban.
 *
 * Se recorre lo que DEVOLVIO el backend en vez de pintar `creadas` y
 * `actualizadas` a mano: un escritor que empiece a omitir filas por un motivo
 * nuevo tiene que verse en la pantalla el mismo dia, no cuando alguien note
 * que faltan datos. Un contador desconocido sale con su nombre crudo, que es
 * feo y es correcto — esconderlo no lo seria.
 */
export function lineasDelResultado(resultado: Record<string, unknown>): LineaDeResultado[] {
  const lineas: LineaDeResultado[] = []
  for (const [clave, valor] of Object.entries(resultado)) {
    if (NO_SON_CONTADORES.has(clave)) continue
    if (typeof valor !== "number") continue
    const esOmision = clave.startsWith("omitidas") || clave.startsWith("rechazadas")
    lineas.push({
      clave,
      etiqueta: ETIQUETAS[clave] ?? clave.replace(/_/g, " "),
      cantidad: valor,
      esOmision,
    })
  }
  // Primero lo que entró, después lo que no: se lee «12 creadas, 3 omitidas».
  return lineas.sort((a, b) => Number(a.esOmision) - Number(b.esOmision))
}

/** Titular honesto del resultado: si no entró nada, lo dice. */
export function titularDelResultado(resultado: Record<string, unknown>): string {
  const lineas = lineasDelResultado(resultado)
  const entraron = lineas.filter((l) => !l.esOmision).reduce((s, l) => s + l.cantidad, 0)
  const omitidas = lineas.filter((l) => l.esOmision).reduce((s, l) => s + l.cantidad, 0)
  if (entraron === 0 && omitidas > 0) return "No entró ninguna fila"
  if (entraron === 0) return "No se escribió nada"
  return `${entraron} ${entraron === 1 ? "fila cargada" : "filas cargadas"}`
}

// ---------------------------------------------------------------------------
// Los errores que vienen en un 422
// ---------------------------------------------------------------------------

/**
 * Las filas malas que trae un rechazo del backend.
 *
 * El `POST /carga/{plantilla}` responde 422 con un OBJETO —mensaje, errores y
 * cuantas filas eran validas— y no con un texto. Si no se desempaqueta, el
 * usuario ve «Datos invalidos.» y pierde las lineas, que es lo unico con lo
 * que puede arreglar el archivo.
 */
export function erroresDelRechazo(error: unknown): {
  mensaje: string | null
  errores: ErrorDeFila[]
  filasValidas: number | null
} {
  const vacio = { mensaje: null, errores: [], filasValidas: null }
  if (!(error instanceof ApiError)) return vacio
  const d = error.detail
  if (!d || typeof d !== "object" || Array.isArray(d)) {
    return { mensaje: typeof d === "string" ? d : null, errores: [], filasValidas: null }
  }
  const obj = d as Record<string, unknown>
  const crudos = Array.isArray(obj.errores) ? obj.errores : []
  return {
    mensaje: typeof obj.mensaje === "string" ? obj.mensaje : null,
    errores: crudos.filter(esErrorDeFila),
    filasValidas: typeof obj.filas_validas === "number" ? obj.filas_validas : null,
  }
}

function esErrorDeFila(x: unknown): x is ErrorDeFila {
  return Boolean(x) && typeof x === "object" && typeof (x as ErrorDeFila).linea === "number"
}

/**
 * Los primeros N errores y cuantos quedan fuera.
 *
 * Con un archivo de quinientas filas mal exportado salen quinientos errores, y
 * pintarlos todos deja la pantalla inservible justo cuando hay que leerla. Se
 * muestran los primeros —que casi siempre son el mismo problema repetido— y se
 * DICE cuantos faltan, en vez de cortar en silencio.
 */
export function primerosErrores(
  errores: ErrorDeFila[],
  maximo = 25,
): { mostrados: ErrorDeFila[]; ocultos: number } {
  const ordenados = [...errores].sort((a, b) => a.linea - b.linea)
  return {
    mostrados: ordenados.slice(0, maximo),
    ocultos: Math.max(0, ordenados.length - maximo),
  }
}

/** Como se lee un separador en pantalla. El `;` es invisible en una frase. */
export function nombreDelSeparador(separador: string): string {
  if (separador === ";") return "punto y coma (;)"
  if (separador === ",") return "coma (,)"
  if (separador === "\t") return "tabulación"
  return `«${separador}»`
}
